import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, Spinner, useMediaQuery } from '@chakra-ui/react';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { usePathname } from 'next/navigation';
import { shallowEqual } from 'react-redux';
import { useAccount } from 'wagmi';

import { WagmiConfig } from '@/config/wagmi.config';
import { useChainData } from '@/providers/ChainDataProvider';
import { updateWalletData, useAppDispatch, useAppSelector } from '@/store';
import { getCurrentChain } from '@/utils/common';

import { SCANNERLINK } from '../../constants/constants';
import { EVENTS } from '../../constants/events';
import {
  BLOCKCHAIN_EXPLORER,
  CONNECT_WALLET,
  COPY_ADDRESS,
  DISCONNECT_WALLET,
  WALLET_MODAL,
} from '../../constants/firebase';
import useDomainResolution from '../../hooks/useDomainResolution';
import { emitEvent } from '../../utils/common';
import { trackEvent } from '../../utils/firebase';
import AccountModal from '../AccountModal';
import WalletInfo from '../WalletInfo';
import WalletMessageModal from '../WalletMessageModal';

const HeaderWalletConnect: React.FC<{ isEmbed: boolean }> = ({ isEmbed }) => {
  const pathname = usePathname();

  const token = getCurrentChain(pathname);
  const [isMobileOrTablet] = useMediaQuery('(max-width: 768px)');
  const dispatch = useAppDispatch();
  const firebaseEventsData = useAppSelector(
    (state) => state.event.firebaseEventsData,
  );
  const { isLoading, walletModal, isWrongChain } = useAppSelector(
    (state) => state.wallet,
    shallowEqual,
  );
  const isWalletModalOpen = useAppSelector(
    (state) => state.stake.isWalletModalOpen,
  );

  const {
    chain,
    isConnected,
    address: walletAddress,
    connector: activeConnector,
  } = useAccount<WagmiConfig>();

  const { openConnectModal } = useConnectModal();

  const { isSafeApp } = useAppSelector((state) => state.user);

  const domain = useDomainResolution();

  const { switchChain } = useChainData();

  const [isOpenAccountModal, setIsOpenAccountModal] = useState(false);

  const tractFirebaseEvent = useCallback(
    (eventName: string, eventParams = {}) => {
      trackEvent(eventName, { ...firebaseEventsData, ...eventParams });
    },
    [firebaseEventsData],
  );

  const handleConnectWallet = useCallback(() => {
    openConnectModal?.();
    tractFirebaseEvent(CONNECT_WALLET, { wallet_cta: 'top right' });
  }, [dispatch, isMobileOrTablet, tractFirebaseEvent, openConnectModal]);

  const onCloseAccountModal = useCallback((isWalletDisconnect: boolean) => {
    setIsOpenAccountModal(false);
    if (isWalletDisconnect) {
      tractFirebaseEvent(DISCONNECT_WALLET);
      emitEvent(EVENTS.HANDLE_DISCONNECT);
    }
  }, []);

  useEffect(() => {
    const elements = [
      document.querySelector('html'),
      document.querySelector(' body'),
    ];
    if (!isWalletModalOpen) {
      elements.forEach((element) => element?.classList.add('enableScroll'));
    } else {
      elements.forEach((element) => element?.classList.remove('enableScroll'));
    }
  }, [isWalletModalOpen]);

  const closeWalletMessageModal = () => {
    dispatch(
      updateWalletData({
        walletModal: {
          isVisible: false,
          error: '',
        },
      }),
    );
  };

  const loadingButton = useMemo(
    () => (
      <Button
        variant="secondary"
        // onClick={() => {}}
        borderRadius="0.5rem"
        disabled
      >
        <Spinner size="md" color="primary" />
      </Button>
    ),
    [],
  );

  return (
    <>
      <Box ml="1rem">
        {isLoading && loadingButton}
        {!isLoading &&
          (isConnected && activeConnector ? (
            <WalletInfo
              activeConnector={activeConnector}
              walletAddress={walletAddress}
              onCopyAddress={() => tractFirebaseEvent(COPY_ADDRESS)}
              onClick={(isAccountModalVisible) => {
                setIsOpenAccountModal(isAccountModalVisible);
                tractFirebaseEvent(WALLET_MODAL);
              }}
              domain={domain}
            />
          ) : (
            // Embedded hosts (Ledger Wallet, Safe) auto-connect, and `isLoading`
            // already covers that attempt. Once it has settled without a connection
            // we must still offer the button -- otherwise the embed is stuck on a
            // spinner with no way to connect.
            <>
              <Button
                variant="secondary"
                onClick={handleConnectWallet}
                borderRadius="0.5rem"
              >
                Connect wallet
              </Button>
            </>
          ))}
      </Box>

      {walletModal.isVisible && (
        <WalletMessageModal
          isOpen={walletModal.isVisible}
          closeModal={closeWalletMessageModal}
          error={walletModal.error}
          isWrongChain={isWrongChain}
          onSubmitPrimary={switchChain}
          isEmbed={isEmbed || isSafeApp}
        />
      )}

      {isOpenAccountModal && activeConnector && (
        <AccountModal
          isOpen={isOpenAccountModal}
          onCancel={onCloseAccountModal}
          onCopyAddress={() =>
            tractFirebaseEvent(COPY_ADDRESS, { type: 'wallet_modal' })
          }
          scannerLink={`${chain?.blockExplorers?.default.url}${SCANNERLINK[token]}${walletAddress}`}
          onClickScanner={() => tractFirebaseEvent(BLOCKCHAIN_EXPLORER)}
          walletAddress={walletAddress}
          domain={domain}
          activeConnector={activeConnector}
        />
      )}
    </>
  );
};

export default HeaderWalletConnect;
